import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowDown, Phone } from "lucide-react";
import { EASE, MaskLine } from "@/components/Reveal";

const BLUE_TRUCK = "/trucks/blue-truck.jpg";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -70]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), {
    stiffness: 120,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), {
    stiffness: 120,
    damping: 18,
  });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-screen items-center overflow-hidden pb-20 pt-32"
    >
      <div aria-hidden className="bg-grid absolute inset-0" />
      <motion.div
        aria-hidden
        style={{ y: glowY }}
        className="absolute -top-40 right-[-12%] h-[42rem] w-[42rem] rounded-full bg-cobalt/25 blur-[140px]"
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <MaskLine delay={0.1}>
            <span className="inline-flex items-center gap-3 rounded-full border border-white/15 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.3em] text-white/60 sm:text-xs">
              <span className="animate-pulse-dot h-2 w-2 rounded-full bg-glow" />
              Omaha, Nebraska · US DOT 3452744
            </span>
          </MaskLine>

          <h1 className="mt-8 font-display text-[clamp(3.5rem,10vw,8.5rem)] font-extrabold uppercase leading-[0.9]">
            <MaskLine delay={0.2}>
              <span className="block">Dry Van.</span>
            </MaskLine>
            <MaskLine delay={0.32}>
              <span className="block text-cobalt">Reefer.</span>
            </MaskLine>
            <MaskLine delay={0.44}>
              <span className="text-outline block">Delivered.</span>
            </MaskLine>
          </h1>

          <MaskLine delay={0.6}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">
              ZAKI Logistics LLC is an Omaha-based carrier running dry van and
              refrigerated freight. Call and you talk to the owner — not a call
              center.
            </p>
          </MaskLine>

          <MaskLine delay={0.72}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href="tel:+14026391139"
                data-testid="hero-call-button"
                className="group flex items-center gap-3 rounded-full bg-cobalt px-7 py-4 text-base font-bold transition-all duration-300 hover:bg-white hover:text-ink"
              >
                <Phone className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" />
                Call 402-639-1139
              </a>
              <a
                href="#fleet"
                data-testid="hero-fleet-link"
                className="rounded-full border border-white/25 px-7 py-4 text-base font-semibold text-white/85 transition-colors duration-300 hover:border-cobalt hover:text-white"
              >
                See the fleet
              </a>
            </div>
          </MaskLine>
        </div>

        <div className="lg:col-span-5 [perspective:1200px]">
          <motion.div
            style={{ y: imgY, rotateX, rotateY, transformStyle: "preserve-3d" }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
            className="relative"
          >
            <div className="clip-frame relative overflow-hidden border border-white/15 bg-panel">
              <img
                src={BLUE_TRUCK}
                alt="ZAKI Logistics blue Freightliner tractor, unit 572, with the company door decal"
                className="aspect-[4/5] h-full w-full object-cover"
                data-testid="hero-truck-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-ink/10" />
              <div className="absolute bottom-4 left-4 border border-white/20 bg-ink/60 px-3 py-1.5 font-mono text-[10px] tracking-[0.3em] text-white/80 backdrop-blur">
                UNIT 572 · ZAKI FLEET
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 items-center gap-2 font-mono text-[10px] tracking-[0.35em] text-white/40 md:flex">
        <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
        SCROLL
      </div>
    </section>
  );
}
